import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "preact/hooks";
import { feel } from "../feel/kit.js";
import { reduceMotion } from "../settings/settings.js";
import { Bean } from "../ui/Bean.js";
import { Stars } from "../ui/Stars.js";
import type { Chapter, MapMission, Stop } from "./map.js";
import { mapOf, sway } from "./map.js";

/**
 * The mission map (P5-UX-01, look A): the game's art over a winding trail of stops, chapter by
 * chapter, your bean on the next one, finished chapters folded to their signpost. It stays a list:
 * each stop is a button in order, named by its mission, its stars and why it's locked, so screen
 * readers and keyboards go through it as before. Solo starts a stop when it's tapped; the room's
 * lobby picks it (`pressed`) for the host's Start.
 */
export function MissionMap(props: {
  missions: readonly MapMission[];
  /** The game's wide art (its site art), as a strip over the map. */
  art?: string | undefined;
  /** The room's picked mission, shown pressed. */
  pressed?: string | null;
  /** A line under a stop, e.g. a campaign run's best. */
  note?: (id: string) => string | undefined;
  /** Missions the last play opened (P5-UX-03): each opens with its sound, the bean arrives. */
  fresh?: readonly string[] | undefined;
  onPick: (id: string) => void;
}) {
  const map = useMemo(() => mapOf(props.missions), [props.missions]);
  const [unfolded, setUnfolded] = useState<ReadonlySet<string>>(new Set());
  const trail = useRef<HTMLDivElement>(null);
  const [path, setPath] = useState({ d: "", w: 0, h: 0 });
  // A finished chapter folds to its signpost, unless it holds the room's pick.
  const folded = (c: Chapter) =>
    c.done && !unfolded.has(c.name) && !c.stops.some((s) => s.id === props.pressed);

  // The dotted path through the stops as they're laid out, redrawn when the trail changes size.
  useLayoutEffect(() => {
    const box = trail.current;
    if (!box) return;
    const draw = () => {
      const at = box.getBoundingClientRect();
      const pts = [...box.querySelectorAll(".map-dot")].map((dot) => {
        const r = dot.getBoundingClientRect();
        return [r.left - at.left + r.width / 2, r.top - at.top + box.scrollTop + r.height / 2];
      });
      const d = pts
        .map(([x = 0, y = 0], i) => {
          if (i === 0) return `M${x.toFixed(1)} ${y.toFixed(1)}`;
          const [px = 0, py = 0] = pts[i - 1] ?? [];
          const my = ((py + y) / 2).toFixed(1);
          return `C${px.toFixed(1)} ${my} ${x.toFixed(1)} ${my} ${x.toFixed(1)} ${y.toFixed(1)}`;
        })
        .join(" ");
      const w = box.clientWidth;
      const h = box.scrollHeight;
      setPath((p) => (p.d === d && p.w === w && p.h === h ? p : { d, w, h }));
    };
    draw();
    const observer = new ResizeObserver(draw);
    observer.observe(box);
    return () => {
      observer.disconnect();
    };
  }, [map, unfolded]);

  // Opens at your next mission, without moving the page around it: its chapter's signpost at the
  // top when the mission fits under it, otherwise the mission in the middle.
  useLayoutEffect(() => {
    const box = trail.current;
    const here = box?.querySelector<HTMLElement>(".map-stop.current");
    const sign = here?.closest(".map-chapter")?.querySelector(".map-sign");
    if (!box || !here || !sign) return;
    const at = box.getBoundingClientRect().top;
    const signTop = sign.getBoundingClientRect().top - at;
    const hereBottom = here.getBoundingClientRect().bottom - at;
    const hereMiddle = hereBottom - here.offsetHeight / 2;
    box.scrollTop = Math.max(
      0,
      hereBottom - signTop < box.clientHeight - 24
        ? signTop - 4
        : hereMiddle - box.clientHeight / 2,
    );
  }, []);

  const jump = (name: string) => {
    setUnfolded((s) => new Set([...s, name]));
    requestAnimationFrame(() => {
      const box = trail.current;
      const sign = box?.querySelector<HTMLElement>(`[data-chapter="${CSS.escape(name)}"]`);
      if (!box || !sign) return;
      const still = reduceMotion.peek();
      const top = sign.getBoundingClientRect().top - box.getBoundingClientRect().top;
      box.scrollTo({ top: box.scrollTop + top - 8, behavior: still ? "auto" : "smooth" });
    });
  };

  // The moment a play opened something (P5-UX-03): the sound once, the stops marked fresh.
  const freshKey = (props.fresh ?? []).join(",");
  useEffect(() => {
    if (freshKey === "") return;
    const t = setTimeout(
      () => {
        feel("unlock");
      },
      reduceMotion.peek() ? 0 : 400,
    );
    return () => {
      clearTimeout(t);
    };
  }, [freshKey]);
  const pass = {
    pressed: props.pressed,
    note: props.note,
    onPick: props.onPick,
    fresh: props.fresh ?? [],
  };
  let index = 0; // each stop's place on the whole trail, folded or not, so the sway stays put
  return (
    <div class="map">
      {props.art && <img class="map-art" src={props.art} alt="" width={640} height={160} />}
      {map.beside.length > 0 && (
        <ul class="map-beside">
          {map.beside.map((s) => (
            <li key={s.id}>
              <StopButton stop={s} {...pass} chip />
            </li>
          ))}
        </ul>
      )}
      {map.chapters.length > 2 && (
        <div class="map-jump" role="group" aria-label="Chapters">
          {map.chapters.map((c) => (
            <button
              key={c.name}
              type="button"
              class={c.done ? "done" : ""}
              onClick={() => {
                jump(c.name);
              }}
            >
              {c.name}{" "}
              <span class="count">
                {c.done ? "✓" : `${String(c.won)}/${String(c.stops.length)}`}
              </span>
            </button>
          ))}
        </div>
      )}
      <div class="map-trail" ref={trail}>
        <svg
          class="map-path"
          aria-hidden="true"
          width={path.w}
          height={path.h}
          viewBox={`0 0 ${String(path.w)} ${String(path.h)}`}
        >
          <path d={path.d} />
        </svg>
        {map.chapters.map((c) => {
          const first = index;
          index += c.stops.length;
          const shut = folded(c);
          const sum = c.done ? "✓ Done" : `${String(c.won)} of ${String(c.stops.length)} won`;
          return (
            <section key={c.name} class="map-chapter">
              <h3 class={`map-sign${c.done ? " done" : ""}`} data-chapter={c.name}>
                {c.done ? (
                  <button
                    type="button"
                    aria-expanded={!shut}
                    onClick={() => {
                      setUnfolded((s) => {
                        const next = new Set(s);
                        if (shut) next.add(c.name);
                        else next.delete(c.name);
                        return next;
                      });
                    }}
                  >
                    <span>{c.name}</span> <span class="sum">{sum}</span>
                  </button>
                ) : (
                  <>
                    <span>{c.name}</span> <span class="sum">{sum}</span>
                  </>
                )}
              </h3>
              {!shut && (
                <ol class="map-stops">
                  {c.stops.map((s, i) => (
                    <li
                      key={s.id}
                      class={s.current ? "here" : ""}
                      style={{ "--sway": String(sway(first + i)) }}
                    >
                      <StopButton
                        stop={s}
                        {...pass}
                        n={i + 1}
                        // Why it's locked, said under the next one to open; the rest say it unseen.
                        saysWhy={!s.open && (i === 0 || c.stops[i - 1]?.open === true)}
                      />
                    </li>
                  ))}
                </ol>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}

function StopButton(props: {
  stop: Stop;
  pressed?: string | null | undefined;
  note?: ((id: string) => string | undefined) | undefined;
  onPick: (id: string) => void;
  fresh?: readonly string[];
  chip?: boolean;
  n?: number;
  saysWhy?: boolean;
}) {
  const s = props.stop;
  const note = props.note?.(s.id);
  const look = [
    props.chip ? "map-chip" : "map-stop",
    s.stars !== undefined ? "won" : "",
    s.current ? "current" : "",
    s.open ? "" : "locked",
    props.fresh?.includes(s.id) ? "fresh" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button
      type="button"
      class={look}
      disabled={!s.open}
      {...(props.pressed === undefined ? {} : { "aria-pressed": props.pressed === s.id })}
      onClick={() => {
        props.onPick(s.id);
      }}
    >
      {!props.chip && (
        <span class="map-dot" aria-hidden="true">
          {s.open ? String(props.n ?? "") : <Lock />}
          {s.current && <Bean class="map-bean" colour={0} size={34} />}
        </span>
      )}
      {props.chip && !s.open && <Lock />}
      <span class="map-name">{s.name}</span>
      <Stars n={s.stars} />
      {/* A Pass mission beside the trail says so in a word; its whole reason is still its name's. */}
      {props.chip && !s.open && s.pack && (
        <span class="tag" aria-hidden="true">
          Pass
        </span>
      )}
      {!s.open && s.why && (
        <span class={(props.chip && !s.pack) || props.saysWhy === true ? "why" : "visually-hidden"}>
          {s.why}
        </span>
      )}
      {note && <span class="why">{note}</span>}
    </button>
  );
}

const Lock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      d="M7 11V8a5 5 0 0 1 10 0v3M6 11h12v9H6z"
    />
  </svg>
);
